// @ts-nocheck
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  HelpCircle,
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
  Edit2,
  BookOpen,
} from 'lucide-react';
import {
  Card,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  Button,
  Badge,
  Input,
  Select,
  StatCard,
  CircularProgress,
} from '@azyntor-labs/nexus-ui';
import { questionsApi, questionBanksApi } from '@/api/endpoints';
import { Question, QuestionType, DifficultyLevel, QuestionBank } from '@/types';

export const QuestionsPage = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState<string>('');
  const [bankFilter, setBankFilter] = useState<string>('');

  // Fetch all question banks for the filter
  const { data: questionBanksResponse } = useQuery({
    queryKey: ['question-banks'],
    queryFn: () => questionBanksApi.list({ page: 1, per_page: 100 }),
  });

  const questionBanks = Array.isArray(questionBanksResponse?.data?.data?.data)
    ? questionBanksResponse.data.data.data
    : [];

  // Fetch questions with filters
  const { data: questionsResponse, isLoading } = useQuery({
    queryKey: ['all-questions', { page, per_page: perPage, question_bank_id: bankFilter, difficulty: difficultyFilter }],
    queryFn: () => questionsApi.list({
      page,
      per_page: perPage,
      question_bank_id: bankFilter || undefined,
      difficulty: difficultyFilter || undefined
    }),
  });

  // Fetch difficulty counts (for stats - fetches count for each difficulty)
  const { data: easyCountResponse } = useQuery({
    queryKey: ['questions-count-easy', { question_bank_id: bankFilter }],
    queryFn: () => questionsApi.list({
      page: 1,
      per_page: 1,
      question_bank_id: bankFilter || undefined,
      difficulty: 'easy'
    }),
  });

  const { data: mediumCountResponse } = useQuery({
    queryKey: ['questions-count-medium', { question_bank_id: bankFilter }],
    queryFn: () => questionsApi.list({
      page: 1,
      per_page: 1,
      question_bank_id: bankFilter || undefined,
      difficulty: 'medium'
    }),
  });

  const { data: hardCountResponse } = useQuery({
    queryKey: ['questions-count-hard', { question_bank_id: bankFilter }],
    queryFn: () => questionsApi.list({
      page: 1,
      per_page: 1,
      question_bank_id: bankFilter || undefined,
      difficulty: 'hard'
    }),
  });

  // Extract questions array from nested response
  const questions = Array.isArray(questionsResponse?.data?.data?.data)
    ? questionsResponse.data.data.data
    : [];
  const total = (questionsResponse?.data?.data as any)?.total || 0;
  const totalPages = Math.ceil(total / perPage) || 1;

  // Get difficulty counts from separate queries
  const easyCount = (easyCountResponse?.data?.data as any)?.total || 0;
  const mediumCount = (mediumCountResponse?.data?.data as any)?.total || 0;
  const hardCount = (hardCountResponse?.data?.data as any)?.total || 0;
  const totalQuestions = difficultyFilter ? total : easyCount + mediumCount + hardCount;

  // Filter questions by search term (only if search is active)
  const filteredQuestions = searchTerm
    ? questions.filter((question: Question) => {
        const searchLower = searchTerm.toLowerCase();
        return question.question_text.toLowerCase().includes(searchLower);
      })
    : questions;

  const difficultyOptions = [
    { value: '', label: 'All Difficulties' },
    { value: 'easy', label: 'Easy' },
    { value: 'medium', label: 'Medium' },
    { value: 'hard', label: 'Hard' },
  ];

  const bankOptions = [
    { value: '', label: 'All Question Banks' },
    ...questionBanks.map((bank: QuestionBank) => ({
      value: bank.id,
      label: bank.name,
    })),
  ];

  const getDifficultyBadgeVariant = (difficulty: DifficultyLevel) => {
    switch (difficulty) {
      case 'easy':
        return 'success';
      case 'medium':
        return 'warning';
      case 'hard':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getQuestionTypeBadgeVariant = (type: QuestionType) => {
    switch (type) {
      case 'mcq':
        return 'primary';
      case 'true_false':
        return 'info';
      case 'short_answer':
        return 'warning';
      case 'essay':
        return 'secondary';
      case 'coding':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getQuestionBankName = (bankId: string) => {
    const bank = questionBanks.find((b: QuestionBank) => b.id === bankId);
    return bank?.name || 'Unknown';
  };

  const handleViewQuestion = (question: Question) => {
    navigate(`/teacher/question-banks/${question.question_bank_id}/questions`);
  };

  const handleEditQuestion = (question: Question) => {
    navigate(`/teacher/question-banks/${question.question_bank_id}/questions`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">All Questions</h1>
          <p className="text-white/50 mt-1">Browse and manage questions across all question banks</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard
          title="Total Questions"
          value={totalQuestions}
          icon={<HelpCircle />}
          color="blue"
        />

        <StatCard
          title="Easy"
          value={easyCount}
          icon={<HelpCircle />}
          trendValue={totalQuestions > 0 ? `${Math.round((easyCount / totalQuestions) * 100)}%` : undefined}
          color="green"
          chart={<CircularProgress value={totalQuestions > 0 ? (easyCount / totalQuestions) * 100 : 0} size={48} />}
        />

        <StatCard
          title="Medium"
          value={mediumCount}
          icon={<HelpCircle />}
          trendValue={totalQuestions > 0 ? `${Math.round((mediumCount / totalQuestions) * 100)}%` : undefined}
          color="orange"
          chart={<CircularProgress value={totalQuestions > 0 ? (mediumCount / totalQuestions) * 100 : 0} size={48} />}
        />

        <StatCard
          title="Hard"
          value={hardCount}
          icon={<HelpCircle />}
          trendValue={totalQuestions > 0 ? `${Math.round((hardCount / totalQuestions) * 100)}%` : undefined}
          color="red"
          chart={<CircularProgress value={totalQuestions > 0 ? (hardCount / totalQuestions) * 100 : 0} size={48} />}
        />
      </div>

      {/* Filters */}
      <Card>
        <div className="p-4 border-b border-white/[0.06]">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftIcon={<Search className="w-4 h-4 text-white/40" />}
              />
            </div>
            <div className="w-full sm:w-48">
              <Select
                value={bankFilter}
                onChange={(e) => {
                  setBankFilter(e.target.value);
                  setPage(1);
                }}
                options={bankOptions}
              />
            </div>
            <div className="w-full sm:w-48">
              <Select
                value={difficultyFilter}
                onChange={(e) => {
                  setDifficultyFilter(e.target.value);
                  setPage(1);
                }}
                options={difficultyOptions}
              />
            </div>
          </div>
        </div>

        {/* Questions Table */}
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-white/50">Loading questions...</p>
          </div>
        ) : filteredQuestions.length === 0 ? (
          <div className="p-8 text-center">
            <HelpCircle className="w-12 h-12 text-white/30 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white">No questions found</h3>
            <p className="text-white/50 mt-2">
              {searchTerm
                ? 'Try adjusting your search or filters'
                : 'Questions will appear here once they are created'}
            </p>
          </div>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Question</TableHeader>
                  <TableHeader>Question Bank</TableHeader>
                  <TableHeader>Type</TableHeader>
                  <TableHeader>Difficulty</TableHeader>
                  <TableHeader>Points</TableHeader>
                  <TableHeader>Actions</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredQuestions.map((question: Question) => (
                  <TableRow key={question.id}>
                    <TableCell>
                      <div className="max-w-md">
                        <p className="font-medium text-white line-clamp-2">
                          {question.question_text}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <BookOpen className="w-4 h-4 text-white/40" />
                        <span className="text-sm text-white/70">
                          {getQuestionBankName(question.question_bank_id)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getQuestionTypeBadgeVariant(question.question_type)} size="sm">
                        {question.question_type?.replace('_', ' ') || 'N/A'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getDifficultyBadgeVariant(question.difficulty)} size="sm">
                        {question.difficulty || 'N/A'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-white/50">{question.points} pts</TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Eye className="w-3 h-3" />}
                          onClick={() => handleViewQuestion(question)}
                        >
                          View
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Edit2 className="w-3 h-3" />}
                          onClick={() => handleEditQuestion(question)}
                        >
                          Edit
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="p-4 border-t border-white/[0.06] flex items-center justify-between">
              <div className="text-sm text-white/50">
                Showing {((page - 1) * perPage) + 1} to {Math.min(page * perPage, total)} of {total} questions
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPage(p => Math.max(1, p - 1));
                    setSearchTerm(''); // Clear search when changing pages
                  }}
                  disabled={page === 1}
                  leftIcon={<ChevronLeft className="w-4 h-4" />}
                >
                  Previous
                </Button>
                <span className="text-sm text-white/50">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPage(p => Math.min(totalPages, p + 1));
                    setSearchTerm(''); // Clear search when changing pages
                  }}
                  disabled={page >= totalPages}
                  rightIcon={<ChevronRight className="w-4 h-4" />}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};
